import '@components/common/layout/scrollbar.scss';

import { ReactComponent as EmptyPanelDroneIcon } from '@assets/components/panel/notification/empty-panel-drone-icon.svg';
import { IcDrone } from '@assets/icons/32x32';
import { Center, For, HStack, Icon, Spinner, Text, useBreakpointValue, VStack } from '@chakra-ui/react';
import { DraggablePanel } from '@components/ui/draggable';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectActiveSite } from '@redux/authent/authent.selectors';
import { useGetFlightPlansQuery } from '@services/utm/flightPlan.api';
import { sortDate } from '@utils/date.utils';
import { addYears } from 'date-fns';
import { FormattedMessage } from 'react-intl';

import { PopupComponentProps, PopupTypeEnum } from '@/types/popup.types';

import FlightPlanCard from './FlightPlanCard';

export default function FlightPlanHistory({
  mapConstraintsRef,
  top,
  left,
  data,
  onClose,
}: Readonly<PopupComponentProps<PopupTypeEnum.FLIGHT_PLAN_HISTORY>>) {
  const { droneCode, openingDate } = data;
  const activeSite = useAppSelector(selectActiveSite);
  const bodyMaxHeight = useBreakpointValue({ base: 420, '4k': 760 });
  const { data: flightPlans = [], isFetching } = useGetFlightPlansQuery(
    {
      fromDate: addYears(openingDate, -1).toISOString(),
      toDate: openingDate.toISOString(),
      sites: [activeSite!.code],
      droneCodes: [droneCode],
      alsoArchived: true,
    },
    { skip: !activeSite },
  );

  const activations = flightPlans.flatMap((flightPlan) =>
    flightPlan.activations.map((activation) => ({ flightPlan, activation })),
  );

  const drone = flightPlans.flatMap((fp) => fp.drones).find((drone) => drone.code === droneCode);

  return (
    <DraggablePanel.Root constraintsRef={mapConstraintsRef} top={top} left={left}>
      <DraggablePanel.Header onClose={onClose}>
        <FormattedMessage id="flightPlanHistory.title" />
      </DraggablePanel.Header>
      <DraggablePanel.Body width={{ base: '452px', '4k': '520px' }} minHeight={420} maxHeight={bodyMaxHeight}>
        <VStack gap={0.25} backgroundColor="neutral.900" flex={1} minHeight={0}>
          <HStack width="100%" padding="standardM" backgroundColor="neutral.700" gap={2} flexShrink={0}>
            <Icon as={IcDrone} boxSize={{ base: '24px', '4k': '32px' }} color="neutral.white" />
            <Text padding={0.5} textStyle="screenStd" fontWeight="medium" backgroundColor="neutral.black">
              {drone?.functionalName ?? <FormattedMessage id="flightPlanHistory.unknown" />}
            </Text>
          </HStack>
          {isFetching ? (
            <Center width="100%" flex={1}>
              <Spinner boxSize="64px" color="neutral.white" />
            </Center>
          ) : (
            <>
              <Text padding="standardM" backgroundColor="neutral.700" textStyle="screenStd" width="100%">
                <FormattedMessage id="flightPlanHistory.description" values={{ count: activations.length }} />
              </Text>
              <VStack
                width="100%"
                flex={1}
                gap={0.25}
                minHeight={0}
                overflow="hidden"
                overflowY="auto"
                className="map-panel-scrollbar"
              >
                <For
                  each={activations.toSorted(({ activation: a1 }, { activation: a2 }) =>
                    sortDate(a1.endDate, a2.endDate),
                  )}
                  fallback={
                    <Center width="100%" flex={1}>
                      <VStack width="100%">
                        <EmptyPanelDroneIcon />
                        <Text textStyle="md" color="neutral.200">
                          <FormattedMessage id="flightPlanHistory.empty" />
                        </Text>
                      </VStack>
                    </Center>
                  }
                >
                  {(info) => (
                    <FlightPlanCard
                      isSmall
                      isArchived
                      flightPlan={info.flightPlan}
                      activation={info.activation}
                      position={{ x: left, y: top }}
                      key={`${info.flightPlan.code} - ${info.activation.id}`}
                      onClose={onClose}
                    />
                  )}
                </For>
              </VStack>
            </>
          )}
        </VStack>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}
